import { useState, useCallback } from "react";
import { FiChevronDown, FiCheck, FiLock } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { useMemberScores } from "../../../../hooks/usePanelScoringQueries";
import type { PanelMemberWithProgress } from "../../../../types/panel.type";
import type { PanelSupplierScores } from "../../../../types/scoring.type";

type LeadScoreVisibility = "always" | "only_when_not_scoring" | "only_after_all_submitted";

interface MemberScoreAccordionProps {
	rfxId: number;
	evalId: number;
	members: PanelMemberWithProgress[];
	isLeadSubmitted: boolean;
	leadScoreVisibility?: LeadScoreVisibility;
	isLeadScoring?: boolean;
	onEnterScores?: () => void;
}

interface AccordionItemProps {
	rfxId: number;
	evalId: number;
	member: PanelMemberWithProgress;
	isExpanded: boolean;
	isLocked: boolean;
	isLeadRow: boolean;
	onToggle: () => void;
	onEnterScores?: () => void;
}

const SupplierScoreTable: React.FC<{ suppliers: PanelSupplierScores[] }> = ({ suppliers }) => {
	if (suppliers.length === 0) {
		return (
			<p className="px-4 py-3 text-sm text-gray-500">
				No scores available.
			</p>
		);
	}

	return (
		<div className="space-y-4">
			{suppliers.map((supplier) => (
				<div key={supplier.invite_id} className="px-4">
					<h6 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
						{supplier.supplier_name}
					</h6>
					<table className="w-full border-collapse text-sm">
						<thead>
							<tr className="border-b border-gray-100 text-left text-xs text-gray-400">
								<th className="py-1.5 pr-3 font-medium">Criterion</th>
								<th className="py-1.5 pr-3 font-medium text-right w-20">Weight</th>
								<th className="py-1.5 pr-3 font-medium text-right w-20">Score</th>
								<th className="py-1.5 font-medium">Comment</th>
							</tr>
						</thead>
						<tbody>
							{supplier.criteria.map((criterion) => (
								<tr
									key={criterion.rfx_criteria_id}
									className="border-b border-gray-50"
								>
									<td className="py-1.5 pr-3 text-gray-700">
										{criterion.criterion_name}
									</td>
									<td className="py-1.5 pr-3 text-right text-gray-500 tabular-nums">
										{criterion.criterion_weight}%
									</td>
									<td className="py-1.5 pr-3 text-right font-medium tabular-nums">
										{criterion.score !== null ? criterion.score : "—"}
									</td>
									<td className="py-1.5 text-gray-500 text-xs max-w-[200px] truncate">
										{criterion.comment || "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			))}
		</div>
	);
};

const AccordionItem: React.FC<AccordionItemProps> = ({
	rfxId,
	evalId,
	member,
	isExpanded,
	isLocked,
	isLeadRow,
	onToggle,
	onEnterScores,
}) => {
	const { data, isLoading } = useMemberScores(
		isExpanded ? rfxId : undefined,
		isExpanded ? evalId : undefined,
		isExpanded ? member.id : undefined
	);

	const isSubmitted = member.status === "submitted";

	return (
		<div
			className={cn(
				"rounded-lg border transition-colors",
				isExpanded ? "border-[#0031DD]/20 bg-white" : "border-gray-200 bg-white",
				isLocked && "opacity-60"
			)}
			data-testid={`member-accordion-${member.id}`}
		>
			<div
				className={cn(
					"flex w-full items-center justify-between px-4 py-3",
					!isLocked && isSubmitted && "cursor-pointer hover:bg-gray-50",
					"rounded-lg"
				)}
				onClick={!isLocked && isSubmitted ? onToggle : undefined}
				data-testid={`member-accordion-trigger-${member.id}`}
			>
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium text-gray-900">
						{member.name || member.email}
						{member.role === "chair" && (
							<span className="ml-1 text-xs font-normal text-gray-400">(you)</span>
						)}
					</span>
					{isSubmitted && (
						<span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
							<FiCheck size={10} aria-hidden="true" />
							Submitted
						</span>
					)}
					{!isSubmitted && !isLeadRow && (
						<span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
							Not submitted
						</span>
					)}
					{isLeadRow && !isSubmitted && onEnterScores && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onEnterScores();
							}}
							className="inline-flex items-center rounded-md bg-[#0031DD] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0031DD]/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-1"
							data-testid="enter-scores-btn"
						>
							Enter Scores
						</button>
					)}
				</div>
				<div className="flex items-center gap-2">
					{isLocked && !isLeadRow && (
						<span className="inline-flex items-center gap-1 text-xs text-gray-400">
							<FiLock size={12} aria-hidden="true" />
							Submit your scores first
						</span>
					)}
					{!isLocked && isSubmitted && (
						<FiChevronDown
							size={16}
							className={cn(
								"text-gray-400 transition-transform",
								isExpanded && "rotate-180"
							)}
							aria-hidden="true"
						/>
					)}
				</div>
			</div>

			{isExpanded && isSubmitted && (
				<div
					id={`member-scores-${member.id}`}
					role="region"
					aria-label={`Scores for ${member.name || member.email}`}
					className="border-t border-gray-100 py-3"
				>
					{isLoading ? (
						<div className="space-y-2 px-4">
							{[1, 2].map((i) => (
								<SkeletonBase key={i} startColor="lightgray" width="100%" height="32px" className="!rounded" />
							))}
						</div>
					) : (
						<SupplierScoreTable suppliers={data?.suppliers ?? []} />
					)}
				</div>
			)}
		</div>
	);
};

export const MemberScoreAccordion: React.FC<MemberScoreAccordionProps> = ({
	rfxId,
	evalId,
	members,
	isLeadSubmitted,
	leadScoreVisibility = "always",
	isLeadScoring = false,
	onEnterScores,
}) => {
	const [expandedId, setExpandedId] = useState<number | null>(null);

	const handleToggle = useCallback((memberId: number) => {
		setExpandedId((prev) => (prev === memberId ? null : memberId));
	}, []);

	const nonRevokedMembers = members.filter((m) => !m.is_removed && m.status !== "revoked");
	const submittedMembers = nonRevokedMembers.filter((m) => m.status === "submitted");
	const otherMembers = nonRevokedMembers.filter((m) => m.status !== "submitted");
	const sortedMembers = [...submittedMembers, ...otherMembers];

	if (sortedMembers.length === 0) {
		return null;
	}

	// Determine lock state based on visibility setting
	let isLocked = false;
	let lockMessage = "";
	if (leadScoreVisibility === "always") {
		isLocked = false;
	} else if (leadScoreVisibility === "only_when_not_scoring") {
		isLocked = isLeadScoring;
		lockMessage = "You cannot view member scores while you are participating as a scorer.";
	} else if (leadScoreVisibility === "only_after_all_submitted") {
		const allActiveSubmitted = members
			.filter((m) => !m.is_removed)
			.every((m) => m.status === "submitted" || m.status === "declined" || m.status === "revoked");
		isLocked = !allActiveSubmitted;
		lockMessage = "Submit your own scores to view individual panel member scores.";
	}

	return (
		<div data-testid="member-score-accordion">
			<h4 className="mb-3 text-sm font-medium text-gray-700">
				Member Scores
			</h4>
			{isLocked && lockMessage && (
				<div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
					<FiLock size={14} className="shrink-0 text-amber-600" aria-hidden="true" />
					<p className="text-xs text-amber-700">
						{lockMessage}
					</p>
				</div>
			)}
			<div
				className="space-y-2"
				role="group"
				aria-label="Panel member scores"
			>
				{sortedMembers.map((member) => (
					<AccordionItem
						key={member.id}
						rfxId={rfxId}
						evalId={evalId}
						member={member}
						isExpanded={expandedId === member.id}
						isLocked={isLocked}
						isLeadRow={member.role === "chair"}
						onToggle={() => handleToggle(member.id)}
						onEnterScores={onEnterScores}
					/>
				))}
			</div>
		</div>
	);
};
